/**
 * The task timer — **one state, one clock, one writer**, for the whole application.
 *
 * Spec 01 §8 asks for a mini timer in the top bar that works across pages. The bug
 * that requirement invites is the classic one: a timer running in the top bar while
 * a stale copy of the same timer sits on the Time Tracking page, disagreeing with
 * it. `web/shapes/time-tracking.md` §5 is the design decision; this file is how it
 * is made unavailable rather than merely avoided.
 *
 *  1. **One state.** This provider is mounted in `App.tsx` **above the router**,
 *     wrapping `AppShell`, so it outlives every route change. `MiniTimer` in the top
 *     bar and the panel on `/time-tracking` are two readers of one `useTimer()`.
 *     Neither holds a copy and neither fetches: there is no second `TimeEntry`
 *     anywhere in the tree that could go stale, because there is no second fetch to
 *     produce one.
 *
 *  2. **One clock.** `nowMs` is state here and is advanced by the only `setInterval`
 *     in the application. Elapsed seconds are **derived** from the entry against it
 *     by `elapsedSecondsOf` — never accumulated — so two readers rendering in the
 *     same commit read the same instant and cannot show different numbers. See
 *     `elapsed.ts` for why deriving also keeps the browser's number equal to the one
 *     the server will write.
 *
 *  3. **One writer.** `start`, `pause`, `resume` and `stop` are actions on this
 *     value. No screen calls `POST /timers/*` itself, so there is no path by which
 *     one view mutates the timer and another view does not hear about it: every
 *     mutation's response body *replaces* the held entry, in one `setState`, and
 *     both readers re-render from it.
 *
 * `useTimer()` **throws** outside the provider rather than returning a null-object.
 * A screen that renders a timer without joining the single source is the defect this
 * file exists to prevent, and a hook that quietly returned "no timer" would let that
 * screen ship looking like an employee with no timer running.
 *
 * ── Why the entry is read with two requests ─────────────────────────────────
 *
 * `GET /time-entries` is cursor-paginated over a range and makes no promise that a
 * live entry sorts first, so "fetch a page and look for one" would be a guess that
 * holds until somebody's history grows. `status=running` and `status=paused` are
 * published filters that answer exactly the question, and at most one row exists
 * across the two (`WORKFORCE_TIMER_ALREADY_ACTIVE` is what enforces that, on the
 * server, where it belongs). They are issued in parallel; `running` wins if both
 * somehow answer, because a running timer is the one accruing time.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { WorkforceApiError } from '../api/errors';
import { timeEntriesPath, timerActionPath, TIMER_START_PATH } from '../api/paths';
import type { TimeEntry, TimeEntryListResponse, TimerStartResponse } from '../api/types';
import { useSession } from '../auth/SessionProvider';
import { elapsedSecondsOf } from './elapsed';

export type TimerStatus = 'loading' | 'ready' | 'error';

/** What a caller needs to start a timer: the task, and the title as they saw it. */
export interface StartTimerInput {
  readonly taskId: string;
  /** Snapshotted onto the entry so the UI can still name a task achau later loses (D-08). */
  readonly taskTitle?: string;
}

export interface TimerValue {
  readonly status: TimerStatus;
  /** The one live entry — `running` or `paused` — or `null` when none is. */
  readonly entry: TimeEntry | null;
  /** The read failed. Not the same thing as "no timer is running". */
  readonly error: WorkforceApiError | null;
  /** A mutation is in flight. Disables the controls, so two clicks are one request. */
  readonly busy: boolean;
  /** The last mutation's refusal, rendered beside the controls that caused it. */
  readonly actionError: WorkforceApiError | null;
  /** The entry a `switch_from_active` start displaced, so the screen can say what it stopped. */
  readonly switchedFrom: TimeEntry | null;
  /** The one clock. Every elapsed number in the application is derived from this. */
  readonly nowMs: number;
  /** Derived, not accumulated. `0` when nothing is live — and no reader renders it then. */
  readonly elapsedSeconds: number;
  /** Bumped on every successful mutation, so the entries list re-reads exactly then. */
  readonly version: number;
  readonly reload: () => void;
  readonly start: (input: StartTimerInput) => Promise<void>;
  readonly pause: () => Promise<void>;
  readonly resume: () => Promise<void>;
  readonly stop: () => Promise<void>;
}

const TimerContext = createContext<TimerValue | null>(null);

export interface TimerProviderProps {
  readonly children: ReactNode;
  /** Injected by tests so elapsed time is a value and not a race. Defaults to the wall clock. */
  readonly now?: () => number;
  /** Tick interval. One second: the display has second resolution and no more. */
  readonly tickMs?: number;
}

function asApiError(cause: unknown): WorkforceApiError {
  if (cause instanceof WorkforceApiError) return cause;
  return new WorkforceApiError({
    code: 'CLIENT_TRANSPORT_FAILED',
    message: cause instanceof Error ? cause.message : 'request failed',
    status: 0,
  });
}

export function TimerProvider({ children, now, tickMs = 1000 }: TimerProviderProps): ReactNode {
  const { client } = useSession();
  const clock = useMemo(() => now ?? ((): number => Date.now()), [now]);

  const [status, setStatus] = useState<TimerStatus>('loading');
  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [error, setError] = useState<WorkforceApiError | null>(null);
  const [actionError, setActionError] = useState<WorkforceApiError | null>(null);
  const [switchedFrom, setSwitchedFrom] = useState<TimeEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [nonce, setNonce] = useState(0);
  const [nowMs, setNowMs] = useState<number>(() => clock());
  const generation = useRef(0);

  // ── the read ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    generation.current += 1;
    const mine = generation.current;
    setStatus('loading');

    const run = async (): Promise<void> => {
      try {
        const [running, paused] = await Promise.all([
          client.request<TimeEntryListResponse>(timeEntriesPath({ status: 'running', limit: 1 }), {
            signal: controller.signal,
          }),
          client.request<TimeEntryListResponse>(timeEntriesPath({ status: 'paused', limit: 1 }), {
            signal: controller.signal,
          }),
        ]);
        if (generation.current !== mine) return;
        setEntry(running.data[0] ?? paused.data[0] ?? null);
        setError(null);
        setStatus('ready');
      } catch (cause) {
        if (generation.current !== mine) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        // The entry is cleared and the status is `error`, which is **not** the same
        // as `ready` with `entry: null`. A failed read rendered as "no timer running"
        // is the absence-as-zero mistake wearing a different hat: the employee would
        // start a second timer against a first one that is still accruing.
        setEntry(null);
        setError(asApiError(cause));
        setStatus('error');
      }
    };

    void run();
    return () => {
      controller.abort();
    };
  }, [client, nonce]);

  // ── the clock ─────────────────────────────────────────────────────────────
  //
  // It ticks only while something is **running**. A paused entry's elapsed time is
  // its recorded `duration_seconds` and does not move, and an absent one has no
  // number at all, so an interval in either case would be work for nothing.
  //
  // What a tick actually costs, stated rather than waved away: the context value
  // changes, so **every `useTimer()` consumer re-renders once a second** — the mini
  // timer, the timer panel, and (because they read `version` and `busy`) the task
  // search, the entries list and the manual-entry form. It is *not* the whole shell:
  // `children` is the same element object each time, so React reuses that subtree and
  // nothing outside the consumers is touched. Five components against a dozen rows is
  // a trade worth making for a number that cannot drift; splitting the clock into its
  // own context would narrow it to two, and is the first thing to do if a screen with
  // a large table ever joins this provider.
  const isRunning = entry?.status === 'running';
  useEffect(() => {
    if (!isRunning) return;
    setNowMs(clock());
    const handle = setInterval(() => {
      setNowMs(clock());
    }, tickMs);
    return () => {
      clearInterval(handle);
    };
  }, [isRunning, clock, tickMs, entry?.id]);

  const mutate = useCallback(
    async (call: () => Promise<TimeEntry | TimerStartResponse>): Promise<void> => {
      setBusy(true);
      setActionError(null);
      try {
        const updated = await call();
        const displaced = 'switched_from' in updated ? (updated.switched_from ?? null) : null;
        setSwitchedFrom(displaced);
        // A stopped entry is no longer the live one. Reading the status the server
        // returned — rather than assuming which action was called — means a transition
        // the server refused to make cannot leave a stale entry on screen.
        setEntry(updated.status === 'running' || updated.status === 'paused' ? updated : null);
        setStatus('ready');
        setError(null);
        setNowMs(clock());
        setVersion((value) => value + 1);
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setActionError(asApiError(cause));
      } finally {
        setBusy(false);
      }
    },
    [clock],
  );

  const start = useCallback(
    async (input: StartTimerInput): Promise<void> => {
      // `switch_from_active` when something is already live. The alternative is a
      // `409 WORKFORCE_TIMER_ALREADY_ACTIVE` the employee can do nothing useful with,
      // and spec 01 §8's UX list asks for one-click switch by name. See
      // `web/shapes/time-tracking.md` §9 — this is the one thing the client decides,
      // because it is arithmetic about state the client can see and not policy.
      const switching = entry !== null;
      await mutate(() =>
        client.request<TimerStartResponse>(TIMER_START_PATH, {
          method: 'POST',
          body: {
            achau_task_id: input.taskId,
            switch_from_active: switching,
            ...(input.taskTitle === undefined ? {} : { task_title: input.taskTitle }),
          },
        }),
      );
    },
    [client, entry, mutate],
  );

  const act = useCallback(
    async (action: 'pause' | 'resume' | 'stop'): Promise<void> => {
      const live = entry;
      if (live === null) return;
      await mutate(() =>
        client.request<TimeEntry>(timerActionPath(live.id, action), { method: 'POST', body: {} }),
      );
    },
    [client, entry, mutate],
  );

  const pause = useCallback(() => act('pause'), [act]);
  const resume = useCallback(() => act('resume'), [act]);
  const stop = useCallback(() => act('stop'), [act]);
  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  const value = useMemo<TimerValue>(
    () => ({
      status,
      entry,
      error,
      busy,
      actionError,
      switchedFrom,
      nowMs,
      elapsedSeconds: entry === null ? 0 : elapsedSecondsOf(entry, nowMs),
      version,
      reload,
      start,
      pause,
      resume,
      stop,
    }),
    [
      status,
      entry,
      error,
      busy,
      actionError,
      switchedFrom,
      nowMs,
      version,
      reload,
      start,
      pause,
      resume,
      stop,
    ],
  );

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

/**
 * The only way to read or drive the timer.
 *
 * Throws outside the provider. See the module comment: a null-object here would let
 * a screen render a timer that is not the timer.
 */
export function useTimer(): TimerValue {
  const value = useContext(TimerContext);
  if (value === null) {
    throw new Error(
      'useTimer was called outside <TimerProvider>. The timer has exactly one source of ' +
        'truth and it is mounted in App.tsx above the router — join it rather than fetching ' +
        'the running entry again.',
    );
  }
  return value;
}
